import React from "react"
import PropTypes from "prop-types"
import ModalForm from "../../../../components/compounds/modalForm/ModalForm.compunds"
import FormContainer from "../../../../components/layouts/containers/FormContainer.container"
import FieldContainer from "../../../../components/layouts/containers/FieldContainer.container"
import InputRadioButton from "../../../../components/atoms/InputRadioButton.atom"
import DropdownInput from "../../../../components/molecules/DropdownInput.molecule"
import InputTextForm from "../../../../components/molecules/InputTextForm.molecule"
import { UNIFIEDDATE_VALUES } from "../../../../consts/CompoundRangeDatePicker"
import { numberFormatter } from "../../../../utils/numbers.utils"
import { DateTime } from "luxon"
import {
  MODAL_FORM_CLOSE_SPRINT_MODE,
  FIRST_SPRINT_NUMBER,
  CLOSE_TYPES,
} from "../../../../consts/modules/clientReport.consts"
import UnifiedDatePickerForm from "../../../../components/molecules/UnifiedDatePickerForm.molecule"
import TeamMemberMultiselectForm from "../../../../components/molecules/TeamMemberMultiselectForm.molecule"
import Divider from "../../../../components/atoms/Divider.atom"
import ModalFinishClientSprintHeaderTemplate from "./ModalFinishClientSprintHeaderTemplate"
import PointsPerMemberInputs from "./PointsPerMemberInputs"
import useReportFiltersContext from "../../../../hooks/contextConsumers/useReportFiltersContext.hook"
import useClientSprintCloseReasons from "./hooks/useClientSprintCloseReasons.hook"
import { validateFieldValue } from "../../../../utils/validations.utils"
import MilestoneSection from "./MilestoneSection"

const MotivoTimeout = ({
  closeType,
  motivo,
  additionalText,
  updateField,
  setFormData,
}) => {
  const {
    data: closeReasons = [],
    loading,
    error,
  } = useClientSprintCloseReasons(closeType)

  if (loading) return <p>Cargando motivos...</p>
  if (error) return <p>Error al cargar motivos</p>

  const CLOSE_REASONS = closeReasons.map((r) => ({
    label: r.reasonName,
    value: r.id,
    requiresAdditionalText: r.requiresAdditionalText,
  }))

  const requiresText = Boolean(
    CLOSE_REASONS.find((m) => m.value === motivo)?.requiresAdditionalText
  )

  return (
    <div className="col-12 mt-3">
      <label className="text-base font-semibold block mb-2">Motivo</label>

      <DropdownInput
        value={motivo}
        options={CLOSE_REASONS}
        onChange={(e) => {
          const selected = CLOSE_REASONS.find((m) => m.value === e.value)
          updateField("motivo", e.value)
          setFormData((prev) => ({
            ...prev,
            requiresAdditionalText: selected?.requiresAdditionalText || false,
            additionalText:
              selected?.requiresAdditionalText ? prev.additionalText : "",
          }))
        }}
        placeholder="Selecciona un motivo"
        className="w-full"
      />

      {requiresText && (
        <InputTextForm
          className="w-full mt-3"
          placeholder="Especifica brevemente el motivo"
          value={additionalText || ""}
          onChange={(e) => updateField("additionalText", e.target.value)}
        />
      )}
    </div>
  )
}

const ModalFormFinishClientSprint = ({
  visible,
  setVisible,
  sprintData,
  mode = MODAL_FORM_CLOSE_SPRINT_MODE.FINISH,
  loading,
  formData,
  setFormData,
  validations,
  setValidations,
  resetForm,
  updateField,
  isFormValid,
  handleSubmit = () => {},
}) => {
  const { clientReportClients, clientReportSelectedClient } =
    useReportFiltersContext()
  const client = clientReportClients.find(
    (client) => client.clientId === clientReportSelectedClient
  )

  const TotalSprintPoints = () => {
    const totalPoints = formData.teamMembers.reduce(
      (acc, member) => acc + (member.points || 0),
      0
    )
    return (
      <p className="w-full text-right text-xl font-cairo">
        Total: <strong>{`${numberFormatter(totalPoints)} pts`}</strong>{" "}
        realizados del sprint
      </p>
    )
  }

  const hideCloseTypeSection =
    mode === MODAL_FORM_CLOSE_SPRINT_MODE.APPROVE ||
    (mode === MODAL_FORM_CLOSE_SPRINT_MODE.FINISH &&
      (!sprintData?.sprintNumberByClient ||
        sprintData?.sprintNumberByClient === FIRST_SPRINT_NUMBER))

  return (
    <ModalForm
      visible={visible}
      setVisible={setVisible}
      header={
        <ModalFinishClientSprintHeaderTemplate
          mode={mode}
          sprintNumber={sprintData?.sprintNumberByClient ?? FIRST_SPRINT_NUMBER}
          clientCode={sprintData?.clientCode ?? client?.code}
          clientName={sprintData?.clientName ?? client?.name}
        />
      }
      className="add-budget-modal"
      saveLabel="Aceptar"
      service={handleSubmit}
      disabledSaveBtn={loading || !isFormValid()}
      cleanUp={resetForm}
    >
      <FormContainer className="close-sprint-form">
        {!hideCloseTypeSection && (
          <>
            <FieldContainer md={12} className="mb-2">
              <div className="text-base font-semibold leading-[1.5] font-mulish mb-3">
                Tipo de sprint
              </div>
            </FieldContainer>

            <FieldContainer
              md={6}
              className="flex items-center gap-2 min-h-[40px]"
            >
              <InputRadioButton
                className="self-center"
                checked={formData.closeType === CLOSE_TYPES.NORMAL}
                value={CLOSE_TYPES.NORMAL}
                onChange={() =>
                  setFormData((prev) => ({
                    ...prev,
                    closeType: CLOSE_TYPES.NORMAL,
                  }))
                }
              />
              <label className="mt-2px">Normal</label>
            </FieldContainer>

            <FieldContainer
              md={6}
              className="flex items-center gap-2 min-h-[40px]"
            >
              <InputRadioButton
                checked={formData.closeType === CLOSE_TYPES.TIMEOUT}
                value={CLOSE_TYPES.TIMEOUT}
                onChange={() =>
                  setFormData((prev) => ({
                    ...prev,
                    closeType: CLOSE_TYPES.TIMEOUT,
                  }))
                }
              />
              <label className="mt-2px">Time out</label>
            </FieldContainer>
            <FieldContainer md={12} className="py-0">
              <Divider stroke="sm" className="mt-1 mb-1" />
            </FieldContainer>
          </>
        )}
        <FieldContainer md={6}>
          <UnifiedDatePickerForm
            label="Inicio"
            // id="start-date-picker"
            hasSingleWeek={false}
            hasSingleMonth={false}
            hasDayPeriod={false}
            hidePeriodSelector
            hideChevron
            dateRange={[formData.startDate, formData.startDate]}
            setDateRange={(e) => {
              if (formData?.endDate && e[0] > formData.endDate) {
                setFormData((prev) => ({
                  ...prev,
                  endDate: "",
                }))
                setValidations(validateFieldValue(validations, "endDate", ""))
              }
              setFormData((prev) => ({
                ...prev,
                startDate: e[0],
              }))
              setValidations(validateFieldValue(validations, "startDate", e[0]))
            }}
            period={UNIFIEDDATE_VALUES.SINGLEDAY}
            format="dd/MM/yyyy"
            datePickerProps={{
              className: "w-full",
              maxDate: DateTime.now(),
            }}
            disabled={formData.startDate && sprintData?.startDate}
            error={validations.startDate?.errors}
          />
        </FieldContainer>
        <FieldContainer md={6}>
          <UnifiedDatePickerForm
            label="Cierre"
            hasSingleWeek={false}
            hasSingleMonth={false}
            hasDayPeriod={false}
            hidePeriodSelector
            hideChevron
            dateRange={[formData.endDate, formData.endDate]}
            setDateRange={(e) => {
              setFormData((prev) => ({
                ...prev,
                endDate: e[0],
              }))
              setValidations(validateFieldValue(validations, "endDate", e[0]))
            }}
            period={UNIFIEDDATE_VALUES.SINGLEDAY}
            format="dd/MM/yyyy"
            datePickerProps={{
              minDate: formData.startDate,
              maxDate: DateTime.now(),
              className: "w-full",
            }}
            error={validations.endDate?.errors}
            disabled={
              mode === MODAL_FORM_CLOSE_SPRINT_MODE.FINISHED_EDIT ||
              (formData.endDate && sprintData?.endDate)
            }
          />
        </FieldContainer>
        {formData.closeType === CLOSE_TYPES.NORMAL && (
          <FieldContainer md={6}>
            <TeamMemberMultiselectForm
              label="Participantes"
              optionValue="id"
              filterByTeamAreaId={1}
              value={formData.teamMembers?.map((member) => member.teamMemberId)}
              onChange={(selected, teamMemberOptions = []) => {
                const selectedMembers = teamMemberOptions.filter((option) =>
                  selected.includes(option.id)
                )
                setValidations(
                  validateFieldValue(
                    validations,
                    "teamMembers",
                    selectedMembers
                  )
                )
                if (selected.length === 0) {
                  setFormData((prev) => ({
                    ...prev,
                    teamMembers: [],
                  }))
                  return
                }
                setFormData((prev) => ({
                  ...prev,
                  teamMembers: selectedMembers.map((member) => ({
                    teamMemberId: member.id,
                    teamMemberNameTag: member.nameTag,
                    teamMemberName: member.name,
                    teamMemberLastName: member.lastName,
                    points:
                      prev.teamMembers.find((m) => m.teamMemberId === member.id)
                        ?.points || 0,
                  })),
                }))
              }}
              initiallySelected={formData.teamMembers}
              error={validations.teamMembers?.errors}
            />
          </FieldContainer>
        )}
        {formData.closeType === CLOSE_TYPES.TIMEOUT && (
          <MotivoTimeout
            closeType={formData.closeType}
            motivo={formData.motivo}
            additionalText={formData.additionalText}
            updateField={updateField}
            setFormData={setFormData}
          />
        )}
        {mode === MODAL_FORM_CLOSE_SPRINT_MODE.APPROVE && (
          <FieldContainer md={6}>
            <UnifiedDatePickerForm
              label="Aprobación"
              hasSingleWeek={false}
              hasSingleMonth={false}
              hasDayPeriod={false}
              hidePeriodSelector
              hideChevron
              dateRange={[formData.approvalDate, formData.approvalDate]}
              setDateRange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  approvalDate: e[0],
                }))
                setValidations(
                  validateFieldValue(validations, "approvalDate", e[0])
                )
              }}
              period={UNIFIEDDATE_VALUES.SINGLEDAY}
              format="dd/MM/yyyy"
              datePickerProps={{
                minDate: formData.endDate,
                maxDate: DateTime.now(),
                className: "w-full",
              }}
              error={validations.approvalDate?.errors}
            />
          </FieldContainer>
        )}
      </FormContainer>
      {formData.closeType === CLOSE_TYPES.NORMAL && (
        <>
          <Divider stroke="sm" className="mt-1 mb-3" />
          <FormContainer className="points-per-teammember-form">
            <FieldContainer md={12} className="mb-3">
              <h3 className="font-cairo text-xl font-semibold mb-3">
                Puntos por participante
              </h3>
            </FieldContainer>
            <FieldContainer md={12} className="mb-3">
              <PointsPerMemberInputs
                teamMembers={formData.teamMembers}
                setFormData={setFormData}
              />
            </FieldContainer>
            <FieldContainer md={12} className="my-0">
              <TotalSprintPoints />
              {formData.closeType === CLOSE_TYPES.NORMAL && (
                <>
                  <Divider stroke="sm" className="mt-1 mb-3" />
                  <FormContainer>
                    <MilestoneSection
                      formData={formData}
                      setFormData={setFormData}
                    />
                  </FormContainer>
                </>
              )}
            </FieldContainer>
          </FormContainer>
        </>
      )}
    </ModalForm>
  )
}

export default ModalFormFinishClientSprint

ModalFormFinishClientSprint.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  sprintData: PropTypes.object.isRequired,
  mode: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  validations: PropTypes.object.isRequired,
  setValidations: PropTypes.func.isRequired,
  resetForm: PropTypes.func.isRequired,
  updateField: PropTypes.func.isRequired,
  isFormValid: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
}
